import React, { useEffect, useMemo, useState } from 'react';
import { Dialog } from '../ui';
import { Link } from 'react-router-dom';
import { Experience, Content } from '../../hooks/api/useExperiences';
import { ROUTE_PATH } from '../../routes/route-paths';
import { useVimeoPlayer } from '../../hooks/utils/useVimeoPlayer';
import { TopicTag } from '../topic-tag';
import { ExperienceStarButton } from '../favorite-buttons/experience-star-button';
import { useMediaQuery } from '../../hooks/utils/useMediaQuery';
import { useTagGroups } from '../../hooks/api/useTagGroups';

interface VideoPlayModalProps {
  experience: Experience | Content;
  open: boolean;
  onClose: () => void;
  handleScheduleModalOpen?: (experience: Experience | Content) => void;
  selectedTagGroupName?: string;
}

const MAX_PLAYER_WIDTH = 848;
const MAX_PLAYER_HEIGHT = 561;

function useViewportWidth() {
  const [width, setWidth] = useState(() => window.innerWidth);

  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return width;
}

/** 848px is the width where Vimeo draws the volume slider and the rest of the control bar. */
export function playerSizeForViewport(viewportWidth: number) {
  if (viewportWidth >= MAX_PLAYER_WIDTH) {
    return { width: MAX_PLAYER_WIDTH, height: MAX_PLAYER_HEIGHT };
  }
  const width = Math.max(0, viewportWidth);
  return { width, height: Math.round((width * 9) / 16) };
}

const getDisplayTagGroups = (
  tagGroupNames: string[],
  selectedTagGroupName?: string,
  maxCount: number = 3,
): string[] => {
  if (!tagGroupNames?.length) return [];
  // If selectedTagGroupName exists and is in the list, prioritize it first
  if (selectedTagGroupName && tagGroupNames.includes(selectedTagGroupName)) {
    const otherTags = tagGroupNames.filter((name) => name !== selectedTagGroupName);
    return [selectedTagGroupName, ...otherTags].slice(0, maxCount);
  }
  // Otherwise, just return first maxCount
  return tagGroupNames.slice(0, maxCount);
};

export const VideoPlayModal: React.FC<VideoPlayModalProps> = ({ experience, open, onClose, selectedTagGroupName }) => {
  const playbackLocation = 'search-results';
  const isMobile = !useMediaQuery('md');
  const { data: topicTagGroups } = useTagGroups();
  const viewportWidth = useViewportWidth();
  const { width: playerWidth, height: playerHeight } = playerSizeForViewport(viewportWidth);
  const { containerRef } = useVimeoPlayer(experience, playbackLocation, playerWidth, playerHeight);

  const tagGroupNames = useMemo(
    () => (experience as Content)?.tag_group_names || (experience as Experience)?.tag_group_names || [],
    [experience],
  );

  const maxTagCount = isMobile ? 2 : 3;
  const displayTagGroups = useMemo(
    () => getDisplayTagGroups(tagGroupNames, selectedTagGroupName, maxTagCount),
    [tagGroupNames, selectedTagGroupName, maxTagCount],
  );

  const title = useMemo(
    () => experience?.ai_title || experience?.title || `${experience?.name}'s experience`,
    [experience?.ai_title, experience?.title, experience?.name],
  );

  return (
    <>
      <style>
        {`
          @media (max-width: 767px) {
            dialog[open].video-modal-mobile {
              top: auto;
              bottom: 0;
              margin: 0;
              padding: 0 !important;
              height: calc(100vh - 2rem) !important;
              max-height: calc(100vh - 2rem) !important;
            }
            dialog[open].video-modal-mobile > div {
              height: 100% !important;
              max-height: 100% !important;
            }
          }
        `}
      </style>
      <Dialog
        className="video-modal-mobile backdrop:bg-modalBackdropColor rounded-b-0 rounded-t-3 w-full max-w-full overflow-x-hidden bg-white p-0 md:max-w-[848px] md:rounded-xl"
        style={{ touchAction: 'pan-y' }}
        open={open}
        onClose={onClose}
        closeOnOutsideClick={true}
        contentClassName="flex flex-col h-full overflow-hidden md:h-auto md:max-h-[90vh] md:m-auto"
      >
        <div className="relative flex h-full min-h-0 flex-col overflow-hidden">
          {/* Header with close button */}
          <div className="flex shrink-0 items-center justify-end px-4 py-4 md:absolute md:right-10 md:top-4 md:z-30 md:px-0 md:py-0">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              className="text-label-gray flex h-9 w-9 items-center justify-center rounded-full bg-gray-50 hover:bg-gray-100"
              aria-label="Close modal"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M15 5L5 15M5 5L15 15"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          {/* Scrollable content */}
          <div
            className="video-modal-scroll min-h-0 flex-1 overflow-y-auto"
            style={{ scrollbarWidth: 'thin', scrollbarColor: '#d9d9d9 transparent' }}
          >
            <div className="flex flex-col gap-4 overflow-x-hidden pb-4 md:pb-0">
              {/* Video Player. Kept at 848px so Vimeo draws the same control bar as local. */}
              <div className="relative mx-auto" style={{ width: playerWidth }}>
                <div className="rounded-3 overflow-hidden" style={{ width: playerWidth }}>
                  <div className="bg-button-secondary relative" style={{ width: playerWidth, height: playerHeight }}>
                    <div ref={containerRef} className="vimeoEmbed h-full w-full"></div>
                  </div>
                </div>
              </div>

              {/* Content */}
              {displayTagGroups.length > 0 && (
                <div className="flex gap-6 px-4 md:px-[30px]">
                  {displayTagGroups.map((tagGroupName, index) => (
                    <TopicTag key={`${tagGroupName}-${index}`} tagGroupName={tagGroupName} tagGroups={topicTagGroups} />
                  ))}
                </div>
              )}
              <div className="flex w-full items-start justify-between gap-3 px-4 md:items-center md:gap-0 md:px-[30px]">
                <h2 className="font-albert-sans text-label-gray min-w-0 flex-1 text-lg font-medium leading-6 md:text-xl md:leading-7">
                  {title}
                </h2>
                <div className="flex-shrink-0">
                  <ExperienceStarButton experience={experience} />
                </div>
              </div>
              <Link
                to={`${ROUTE_PATH.PEER_DETAILS}?listenerId=${experience.listener_role_id}`}
                className="flex items-center gap-2 px-4 md:px-[30px]"
              >
                <img
                  src={experience.profile_photo_square_file_url}
                  alt={experience.name}
                  className="h-[50px] w-[50px] rounded-full object-cover"
                />
                <p className="text-primary font-albert-sans text-base font-semibold">{experience.name}</p>
              </Link>
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
};
